import { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiUpload } from 'react-icons/fi';
import { useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Modal } from '@/design-system/components/Modal';
import { useToast } from '@/client/hooks/useToast';
import {
  useCreateFieldDefinition,
  useUpdateFieldDefinition,
  useFieldDefinitions,
} from '@/lib/sdk/master-data-field-definitions/queries';
import { useMasterDataDatasets } from '@/lib/sdk/master-data/queries';
import type { MasterDataFieldDefinition, FieldDataType } from '@/lib/sdk/master-data-field-definitions/types';

export const DATA_TYPE_LABELS: Record<FieldDataType, string> = {
  text: 'Text',
  number: 'Number',
  select: 'Select (dropdown)',
  date: 'Date',
  boolean: 'Yes/No',
  image: 'Image (via WhatsApp)',
  managed_reference: 'Reference (search another dataset)',
};

export const DATA_TYPE_COLORS: Record<FieldDataType, string> = {
  text: 'gray',
  number: 'blue',
  select: 'green',
  date: 'orange',
  boolean: 'purple',
  image: 'teal',
  managed_reference: 'pink',
};

const fieldFormSchema = z.object({
  field_name: z.string().min(1, 'Field name is required').regex(/^[a-z0-9_]+$/, 'Use lowercase letters, numbers, underscores only'),
  label: z.string().min(1, 'Label is required'),
  data_type: z.enum(['text', 'number', 'select', 'date', 'boolean', 'image', 'managed_reference']),
  required: z.boolean(),
  hidden: z.boolean(),
  sort_order: z.number().int().min(0),
  options: z.array(z.object({ value: z.string().min(1), label: z.string().min(1) })),
  imageConfig: z.object({
    chatbot_code: z.string(),
    attribute_mappings: z.array(z.object({ field_name: z.string(), task_n: z.string() })),
    watermark_field: z.string().optional(),
  }).optional(),
  referenceDataset: z.string().optional(),
  referenceValueField: z.enum(['key', 'label']).optional(),
}).refine(
  (data) => data.data_type !== 'managed_reference' || !!data.referenceDataset,
  { message: 'Choose a dataset to search', path: ['referenceDataset'] }
);

type FieldFormData = z.infer<typeof fieldFormSchema>;

export interface FieldFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editDef?: MasterDataFieldDefinition | null;
  dataset: string;
  orgId: string;
  nextSortOrder: number;
}

function parseImageConfig(validation: Record<string, unknown> | null | undefined) {
  if (!validation) return { chatbot_code: '', attribute_mappings: [], watermark_field: '' };
  return {
    chatbot_code: (validation.chatbot_code as string) ?? '',
    attribute_mappings: (validation.attribute_mappings as Array<{ field_name: string; task_n: string }>) ?? [],
    watermark_field: (validation.watermark_field as string) ?? '',
  };
}

function parseReferenceConfig(validation: Record<string, unknown> | null | undefined) {
  return {
    referenceDataset: (validation?.reference_dataset as string) ?? '',
    referenceValueField: (validation?.value_field as 'key' | 'label') === 'label' ? 'label' as const : 'key' as const,
  };
}

export function FieldFormModal({ isOpen, onClose, editDef, dataset, orgId, nextSortOrder }: FieldFormModalProps) {
  const toast = useToast();
  const { mutate: create, isPending: isCreating } = useCreateFieldDefinition(orgId);
  const { mutate: update, isPending: isUpdating } = useUpdateFieldDefinition(orgId);

  const csvInputRef = useRef<HTMLInputElement>(null);

  const handleCsvImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
      for (const line of lines) {
        const commaIdx = line.indexOf(',');
        if (commaIdx === -1) {
          const val = line.trim();
          appendOption({ value: val, label: val });
        } else {
          const value = line.slice(0, commaIdx).trim();
          const label = line.slice(commaIdx + 1).trim();
          if (value) appendOption({ value, label: label || value });
        }
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const { register, handleSubmit, control, watch, reset, formState: { errors } } = useForm<FieldFormData>({
    resolver: zodResolver(fieldFormSchema),
    defaultValues: {
      field_name: '',
      label: '',
      data_type: 'text',
      required: false,
      hidden: false,
      sort_order: nextSortOrder,
      options: [],
      imageConfig: { chatbot_code: '', attribute_mappings: [], watermark_field: '' },
      referenceDataset: '',
      referenceValueField: 'key',
    },
  });

  const { fields: optionFields, append: appendOption, remove: removeOption } = useFieldArray({ control, name: 'options' });
  const { fields: mappingFields, append: appendMapping, remove: removeMapping } = useFieldArray({ control, name: 'imageConfig.attribute_mappings' });
  const watchedType = watch('data_type');

  const { data: existingFieldDefs = [] } = useFieldDefinitions(orgId, dataset);
  const selectableFields = existingFieldDefs.filter(
    d => d.field_name !== editDef?.field_name && d.data_type !== 'image'
  );
  const otherManagedReferenceDef = existingFieldDefs.find(
    d => d.data_type === 'managed_reference' && d.field_name !== editDef?.field_name
  );

  const { data: allDatasets = [] } = useMasterDataDatasets(orgId);
  const referenceDatasetOptions = allDatasets.filter(d => d !== dataset);

  useEffect(() => {
    if (isOpen) {
      reset(editDef
        ? {
            field_name: editDef.field_name,
            label: editDef.label,
            data_type: editDef.data_type,
            required: editDef.required,
            hidden: editDef.hidden,
            sort_order: editDef.sort_order,
            options: editDef.options ?? [],
            imageConfig: parseImageConfig(editDef.validation),
            ...parseReferenceConfig(editDef.validation),
          }
        : {
            field_name: '',
            label: '',
            data_type: 'text',
            required: false,
            hidden: false,
            sort_order: nextSortOrder,
            options: [],
            imageConfig: { chatbot_code: '', attribute_mappings: [], watermark_field: '' },
            referenceDataset: '',
            referenceValueField: 'key',
          }
      );
    }
  }, [isOpen, editDef]);

  const handleClose = () => { reset(); onClose(); };

  const onSubmit = (data: FieldFormData) => {
    let validationPayload: Record<string, unknown> | null = null;
    if (data.data_type === 'image' && data.imageConfig) {
      validationPayload = {
        chatbot_code: data.imageConfig.chatbot_code,
        attribute_mappings: data.imageConfig.attribute_mappings,
        ...(data.imageConfig.watermark_field ? { watermark_field: data.imageConfig.watermark_field } : {}),
      };
    } else if (data.data_type === 'managed_reference') {
      validationPayload = {
        reference_dataset: data.referenceDataset,
        value_field: data.referenceValueField ?? 'key',
      };
    }

    if (editDef) {
      update(
        {
          fieldId: editDef.id,
          dto: {
            label: data.label,
            data_type: data.data_type,
            required: data.required,
            hidden: data.hidden,
            options: data.data_type === 'select' ? data.options : null,
            validation: validationPayload,
            sort_order: data.sort_order,
          },
        },
        { onSuccess: () => { toast.success('Field updated'); handleClose(); }, onError: (e: any) => toast.error(e.message ?? 'Update failed') }
      );
    } else {
      create(
        {
          dataset,
          field_name: data.field_name,
          label: data.label,
          data_type: data.data_type,
          required: data.required,
          hidden: data.hidden,
          options: data.data_type === 'select' ? data.options : null,
          validation: validationPayload,
          sort_order: data.sort_order,
        },
        { onSuccess: () => { toast.success('Field created'); handleClose(); }, onError: (e: any) => toast.error(e.message ?? 'Create failed') }
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editDef ? 'Edit Field' : 'Add Field'}
      size="md"
      footer={
        <HStack w="full" justify="flex-end" spacing={3}>
          <Button variant="ghost" onClick={handleClose}>Cancel</Button>
          <Button colorScheme="brand" isLoading={isCreating || isUpdating} onClick={handleSubmit(onSubmit)} data-testid="md-field-form-submit-btn">
            {editDef ? 'Save' : 'Add Field'}
          </Button>
        </HStack>
      }
    >
      <Box maxH="65vh" overflowY="auto" pr={1}>
        <VStack spacing={4} align="stretch">
          <FormControl isInvalid={!!errors.field_name} isRequired isReadOnly={!!editDef}>
            <FormLabel fontSize="sm" color="text-primary" mb={1}>Field Name</FormLabel>
            <Text fontSize="xs" color="text-secondary" mb={2}>Snake_case key stored in database (e.g. material, part_no)</Text>
            <Input {...register('field_name')} placeholder="e.g. material" size="sm" bg={editDef ? 'bg-muted' : 'bg-surface'} borderColor="border-default" isReadOnly={!!editDef} data-testid="md-field-form-name" />
            <FormErrorMessage>{errors.field_name?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.label} isRequired>
            <FormLabel fontSize="sm" color="text-primary" mb={1}>Label</FormLabel>
            <Input {...register('label')} placeholder="e.g. Material" size="sm" bg="bg-surface" borderColor="border-default" data-testid="md-field-form-label" />
            <FormErrorMessage>{errors.label?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.data_type} isRequired>
            <FormLabel fontSize="sm" color="text-primary" mb={1}>Data Type</FormLabel>
            <Select {...register('data_type')} size="sm" bg="bg-surface" borderColor="border-default" data-testid="md-field-form-type">
              {Object.entries(DATA_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v} disabled={v === 'managed_reference' && !!otherManagedReferenceDef}>
                  {l}{v === 'managed_reference' && otherManagedReferenceDef ? ` (already used by ${otherManagedReferenceDef.label})` : ''}
                </option>
              ))}
            </Select>
          </FormControl>

          {watchedType === 'select' && (
            <Box>
              <HStack justify="space-between" mb={2}>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="text-primary">Options</Text>
                  <Text fontSize="xs" color="text-secondary">CSV format: <Text as="span" fontFamily="mono">value,label</Text> per line (label optional)</Text>
                </Box>
                <HStack spacing={1}>
                  <input ref={csvInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleCsvImport} />
                  <Button size="xs" variant="outline" leftIcon={<FiUpload size={10} />} onClick={() => csvInputRef.current?.click()}>Import CSV</Button>
                  <Button size="xs" variant="outline" leftIcon={<FiPlus />} onClick={() => appendOption({ value: '', label: '' })}>Add</Button>
                </HStack>
              </HStack>
              {optionFields.length === 0 && (
                <Text fontSize="sm" color="text-secondary" fontStyle="italic">No options yet.</Text>
              )}
              <VStack spacing={2} align="stretch">
                {optionFields.map((f, i) => (
                  <HStack key={f.id} spacing={2}>
                    <Input {...register(`options.${i}.value`)} placeholder="Value (stored)" size="sm" bg="bg-surface" borderColor="border-default" flex={1} />
                    <Input {...register(`options.${i}.label`)} placeholder="Label (shown)" size="sm" bg="bg-surface" borderColor="border-default" flex={1} />
                    <IconButton aria-label="Remove" icon={<FiTrash2 />} size="sm" variant="ghost" colorScheme="red" onClick={() => removeOption(i)} />
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          {watchedType === 'image' && (
            <Box borderWidth="1px" borderColor="border-default" borderRadius="md" p={3}>
              <Text fontSize="sm" fontWeight="medium" color="text-primary" mb={3}>WhatsApp Upload Configuration</Text>
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" color="text-primary" mb={1}>Chatbot Code</FormLabel>
                  <Text fontSize="xs" color="text-secondary" mb={1}>The code of the bot that collects the image (e.g. ABC123)</Text>
                  <Input
                    {...register('imageConfig.chatbot_code')}
                    placeholder="e.g. ABC123"
                    size="sm"
                    bg="bg-surface"
                    borderColor="border-default"
                    maxLength={10}
                  />
                </FormControl>

                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="text-primary">Attribute Mappings</Text>
                      <Text fontSize="xs" color="text-secondary">Pre-fill bot tasks with master data field values</Text>
                    </Box>
                    <Button size="xs" variant="outline" leftIcon={<FiPlus />} onClick={() => appendMapping({ field_name: '', task_n: '' })}>
                      Add
                    </Button>
                  </HStack>
                  {mappingFields.length === 0 && (
                    <Text fontSize="sm" color="text-secondary" fontStyle="italic">No mappings — bot launches without pre-filled values.</Text>
                  )}
                  <VStack spacing={2} align="stretch">
                    {mappingFields.map((f, i) => (
                      <HStack key={f.id} spacing={2} align="center">
                        <Select
                          {...register(`imageConfig.attribute_mappings.${i}.field_name`)}
                          size="sm"
                          bg="bg-surface"
                          borderColor="border-default"
                          flex={2}
                        >
                          <option value="">Select field…</option>
                          {selectableFields.map(d => (
                            <option key={d.field_name} value={d.field_name}>{d.label} ({d.field_name})</option>
                          ))}
                        </Select>
                        <Text fontSize="sm" color="text-secondary" flexShrink={0}>→ Task #</Text>
                        <Input
                          {...register(`imageConfig.attribute_mappings.${i}.task_n`)}
                          placeholder="N"
                          size="sm"
                          bg="bg-surface"
                          borderColor="border-default"
                          w="60px"
                          flexShrink={0}
                        />
                        <IconButton
                          aria-label="Remove mapping"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => removeMapping(i)}
                        />
                      </HStack>
                    ))}
                  </VStack>
                </Box>

                <FormControl>
                  <FormLabel fontSize="sm" color="text-primary" mb={1}>Watermark Field</FormLabel>
                  <Text fontSize="xs" color="text-secondary" mb={1}>
                    Field whose value is stamped bottom-right on the uploaded image
                  </Text>
                  <Select
                    {...register('imageConfig.watermark_field')}
                    size="sm"
                    bg="bg-surface"
                    borderColor="border-default"
                  >
                    <option value="">None</option>
                    <option value="key">Record Key (key)</option>
                    {selectableFields.map(d => (
                      <option key={d.field_name} value={d.field_name}>{d.label} ({d.field_name})</option>
                    ))}
                  </Select>
                </FormControl>
              </VStack>
            </Box>
          )}

          {watchedType === 'managed_reference' && (
            <Box borderWidth="1px" borderColor="border-default" borderRadius="md" p={3}>
              <Text fontSize="sm" fontWeight="medium" color="text-primary" mb={3}>Reference Configuration</Text>
              <FormControl isInvalid={!!errors.referenceDataset} isRequired>
                <FormLabel fontSize="sm" color="text-primary" mb={1}>Dataset to search</FormLabel>
                <Text fontSize="xs" color="text-secondary" mb={1}>
                  This field replaces the generic &quot;Belongs To&quot; input with a search against the chosen dataset.
                </Text>
                <Select {...register('referenceDataset')} size="sm" bg="bg-surface" borderColor="border-default">
                  <option value="">Select dataset…</option>
                  {referenceDatasetOptions.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.referenceDataset?.message}</FormErrorMessage>
              </FormControl>
              <FormControl mt={3}>
                <FormLabel fontSize="sm" color="text-primary" mb={1}>Value stored in Belongs To</FormLabel>
                <Text fontSize="xs" color="text-secondary" mb={1}>
                  Which field of the picked row gets saved — the unique code, or the display name.
                </Text>
                <Select {...register('referenceValueField')} size="sm" bg="bg-surface" borderColor="border-default">
                  <option value="key">Key (unique code)</option>
                  <option value="label">Label (display name)</option>
                </Select>
              </FormControl>
            </Box>
          )}

          <VStack spacing={3} align="stretch">
            <FormControl display="flex" alignItems="center">
              <Checkbox {...register('required')} colorScheme="brand">Required</Checkbox>
            </FormControl>
            <FormControl display="flex" flexDirection="column" gap={1}>
              <HStack>
                <Switch {...register('hidden')} colorScheme="orange" id="hidden-toggle" />
                <FormLabel htmlFor="hidden-toggle" mb={0} fontSize="sm" color="text-primary" cursor="pointer">
                  Hide in add forms
                </FormLabel>
              </HStack>
              <Text fontSize="xs" color="text-secondary">Field will not appear when adding new records</Text>
            </FormControl>
            <FormControl isInvalid={!!errors.sort_order}>
              <FormLabel fontSize="sm" color="text-primary" mb={1}>Order</FormLabel>
              <Input {...register('sort_order', { valueAsNumber: true })} type="number" size="sm" bg="bg-surface" borderColor="border-default" maxW="100px" />
            </FormControl>
          </VStack>
        </VStack>
      </Box>
    </Modal>
  );
}
